import type { Task, UserAuth } from '@repo/common';
import type { BaseModel, TaskSqliteModel } from '@repo/common/types/Sqlite';
import { TaskPriority, TaskStatus } from '@repo/common/types/Task';

import { parse, stringify } from '@repo/toolslib/src/utils/serialize.util';
import { ValidationUtils } from '@repo/toolslib/src/utils/validation.util';
import { PrivateClientDatabaseAdapter } from '../private.client.adapter';
import type { DeduplicationStrategy } from '../types';
import { convertNullToUndefined } from '../utils';

type TaskSqliteRow = {
  id: string;
  name: string;
  contentHash?: string;
  description?: string;
  status: string;
  priority: string | undefined;
  tags?: string; // JSON string of tag IDs
  createdAt: string;
  dueDate?: string;
  updatedAt: string;
  completedAt?: string;
  deletedAt?: string;
  memoryId?: string | null;
  workspaceId?: string | null;
  data?: string | null; // JSON string
};

export class TaskClientAdapter extends PrivateClientDatabaseAdapter<TaskSqliteRow, Task> {
  constructor(
    sqliteModel: BaseModel<
      Task & { name: string; memoryId: string; workspaceId: string },
      TaskSqliteRow
    >,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: Partial<{
      baseUrl: string;
      authToken: string;
      timeout: number;
      retries: number;
    }>
  ) {
    super(sqliteModel, 'task', userId, authToken, adapterConfig);
  }

  protected getDeduplicationStrategy(): DeduplicationStrategy<Task> {
    return {
      type: 'custom' as const,
      getKey: (item: Task) => {
        // Use contentHash if available, otherwise use id (no deduplication)
        if (item.contentHash) {
          return item.contentHash;
        }
        // If contentHash is null/undefined, use id to avoid deduplicating user-created tasks
        return item.id;
      }
    };
  }

  /**
   * Override validateItemEquality to use the improved validateTaskEquality from ValidationUtils
   * This provides more accurate comparison for task-specific fields and data structures
   */
  protected validateItemEquality(
    item1: Task,
    item2: Task
  ): { isEqual: boolean; differences: string[] } {
    console.log(`[DEBUG] Task client adapter validateItemEquality called`);
    return ValidationUtils.validateTaskEquality(item1, item2);
  }

  protected async upsertListToSqliteImpl(rows: TaskSqliteRow[]): Promise<void> {
    // Convert rows to Task items
    const tasks = rows.map((row) => this.sqliteRowToItem(row));

    // Use the task-specific bulkUpsertOnContentHash method for optimal deduplication
    const taskModel = this.sqliteModel as TaskSqliteModel;
    await taskModel.bulkUpsertOnContentHash(tasks);
  }

  protected async getAllFromSqlite(): Promise<TaskSqliteRow[]> {
    const tasks = await this.sqliteModel.findAllObj({ paranoid: true });
    return tasks.map((task: Task) => {
      const convertedTask = convertNullToUndefined(task);
      if (!convertedTask.name) {
        throw new Error('Task name is required');
      }
      return {
        id: convertedTask.id,
        name: convertedTask.name,
        contentHash: convertedTask.contentHash,
        description: convertedTask.description,
        status: convertedTask.status,
        priority: convertedTask.priority,
        tags: convertedTask.tags ? JSON.stringify(convertedTask.tags) : undefined,
        createdAt: convertedTask.createdAt.toISOString(),
        dueDate: convertedTask.dueDate?.toISOString(),
        updatedAt: convertedTask.updatedAt.toISOString(),
        completedAt: convertedTask.completedAt?.toISOString(),
        deletedAt: convertedTask.deletedAt?.toISOString(),
        memoryId: convertedTask.memoryId ?? undefined,
        workspaceId: convertedTask.workspaceId ?? undefined,
        data: convertedTask.data ? JSON.stringify(convertedTask.data) : null
      };
    });
  }

  protected async getFromSqliteById(id: string): Promise<TaskSqliteRow | null> {
    const tasks = await this.sqliteModel.findAllObj({
      where: { id },
      limit: 1
    });

    if (tasks.length === 0) return null;

    const task = tasks[0];
    const convertedTask = convertNullToUndefined(task);
    if (!convertedTask.name) {
      throw new Error('Task name is required');
    }
    return {
      id: convertedTask.id,
      name: convertedTask.name,
      contentHash: convertedTask.contentHash,
      description: convertedTask.description,
      status: convertedTask.status,
      priority: convertedTask.priority,
      tags: convertedTask.tags ? JSON.stringify(convertedTask.tags) : undefined,
      createdAt: convertedTask.createdAt.toISOString(),
      dueDate: convertedTask.dueDate?.toISOString(),
      updatedAt: convertedTask.updatedAt.toISOString(),
      completedAt: convertedTask.completedAt?.toISOString(),
      deletedAt: convertedTask.deletedAt?.toISOString(),
      memoryId: convertedTask.memoryId ?? undefined,
      workspaceId: convertedTask.workspaceId ?? undefined,
      data: convertedTask.data ? JSON.stringify(convertedTask.data) : null
    };
  }

  protected sqliteRowToItem(row: TaskSqliteRow): Task {
    return {
      id: row.id,
      name: row.name,
      contentHash: row.contentHash,
      description: row.description,
      status: row.status as TaskStatus,
      priority: row.priority && row.priority !== '' ? (row.priority as TaskPriority) : undefined,
      tags: (() => {
        try {
          if (!row.tags || row.tags === '[]') return [];
          return JSON.parse(row.tags);
        } catch (error) {
          console.warn('Failed to parse tags:', row.tags, error);
          return [];
        }
      })(),
      createdAt: new Date(row.createdAt),
      dueDate: row.dueDate ? new Date(row.dueDate) : undefined,
      updatedAt: new Date(row.updatedAt),
      completedAt: row.completedAt ? new Date(row.completedAt) : undefined,
      deletedAt: row.deletedAt ? new Date(row.deletedAt) : undefined,
      memoryId: row.memoryId ?? undefined,
      workspaceId: row.workspaceId ?? undefined,
      data: row.data ? parse(row.data) : undefined
    };
  }

  protected itemToSqliteRow(item: Task): TaskSqliteRow {
    if (!item.name) {
      throw new Error('Task name is required');
    }
    return {
      id: item.id,
      name: item.name,
      contentHash: item.contentHash,
      description: item.description,
      status: item.status,
      priority: item.priority || undefined,
      tags: item.tags ? stringify(item.tags) : undefined,
      createdAt: item.createdAt.toISOString(),
      dueDate: item.dueDate?.toISOString(),
      updatedAt: item.updatedAt.toISOString(),
      completedAt: item.completedAt?.toISOString(),
      deletedAt: item.deletedAt?.toISOString(),
      memoryId: item.memoryId ?? undefined,
      workspaceId: item.workspaceId ?? undefined,
      data: item.data ? stringify(item.data) : null
    };
  }
}
